import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users, 
  Calendar, 
  Clock, 
  Activity,
  FileText,
  Bell
} from 'lucide-react';
import { patients, sessions } from '../services/api';

// Mock appointment data (to be replaced with external API integration)
const mockAppointments = [
  { 
    id: 1,
    time: '09:00',
    patientName: 'John Smith',
    type: 'Consulta Inicial',
    status: 'agendado'
  },
  {
    id: 2,
    time: '11:30',
    patientName: 'Sarah Johnson',
    type: 'Acompanhamento',
    status: 'confirmado'
  },
  {
    id: 3,
    time: '14:00',
    patientName: 'Michael Brown',
    type: 'Reunião com Pais',
    status: 'agendado'
  }
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    patients: 0,
    sessions: 0,
    todayAppointments: 0,
    pendingReports: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching dashboard data...');
        
        // Fetch patients count
        console.log('Fetching patients...');
        const patientsResponse = await patients.getAll();
        console.log('Patients response:', patientsResponse);
        
        // The patients are in patientsResponse.data.data array
        const patientList = patientsResponse?.data?.data || patientsResponse?.data || [];
        const activePatients = Array.isArray(patientList) ? patientList.filter(p => p.status === 'active') : [];
        
        // Fetch sessions
        console.log('Fetching sessions...');
        const sessionsResponse = await sessions.getAll();
        console.log('Sessions response:', sessionsResponse);
        
        // Filter active sessions (scheduled or in-progress)
        const sessionList = sessionsResponse?.data?.data || sessionsResponse?.data || [];
        const safeSessionList = Array.isArray(sessionList) ? sessionList : [];
        const activeSessions = safeSessionList.filter(s =>
          s.status === 'scheduled' || s.status === 'in-progress'
        );

        // Get today's sessions
        const today = new Date().toISOString().split('T')[0];
        const todaySessions = safeSessionList
          .filter(s => s.date === today)
          .map(s => ({
            id: s.id,
            time: s.startTime.substring(0, 5),
            patientName: `${s.patient.firstName} ${s.patient.lastName}`,
            type: s.type,
            status: s.status
          }));
        
        setStats({
          patients: activePatients.length,
          sessions: activeSessions.length,
          todayAppointments: todaySessions.length,
          pendingReports: 0 // This will be implemented later
        });

        // Set today's schedule using actual sessions
        setTodaySchedule(todaySessions);

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-700">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-2 text-red-600 hover:text-red-800 text-sm font-medium"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4 text-gray-800">
            Bem-vindo ao Sistema GiA
          </h3>
          <p className="text-gray-600">
            Este sistema integrado ajuda a gerenciar a terapia do autismo com ferramentas ABA especializadas, análises e relatórios.
          </p>
          <div className="mt-4">
            <button 
              onClick={() => navigate('/dashboard/patients')}
              className="bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-800"
            >
              Ver Pacientes
            </button>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4 text-gray-800">Agenda de Hoje</h3>
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
            </div>
          ) : todaySchedule.length > 0 ? (
            <div className="space-y-3">
              {todaySchedule.map((appointment) => (
                <div key={appointment.id} className="flex justify-between items-center p-2 bg-brand-50 rounded">
                  <div>
                    <p className="font-medium">{appointment.time}</p>
                    <p className="text-sm text-gray-600">{appointment.patientName}</p>
                    <p className="text-xs text-gray-500">{appointment.type}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    appointment.status === 'confirmado' || appointment.status === 'em-andamento'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600">Nenhuma consulta agendada para hoje</p>
          )}
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4 text-gray-800">Status do Sistema</h3>
          <div className="space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
              </div>
            ) : (
              <>
                <div className="flex items-center text-green-600">
                  <div className="w-2 h-2 bg-green-600 rounded-full mr-2"></div>
                  <span>Todos os sistemas operacionais</span>
                </div>
                <div className="mt-4">
                  <p className="text-sm text-gray-600">Última atualização: {new Date().toLocaleTimeString()}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <button 
          onClick={() => navigate('/dashboard/patients')}
          className="bg-brand-700 text-white rounded-lg shadow p-4 flex items-center hover:bg-brand-800 transition-colors"
        >
          <div className="rounded-full bg-brand-800 p-3 mr-3">
            <Users size={20} />
          </div>
          <div>
            <p className="text-sm opacity-80">Pacientes</p>
            <p className="text-xl font-bold">{loading ? '...' : `${stats.patients} Ativos`}</p>
          </div>
        </button>
        
        <div className="bg-green-600 text-white rounded-lg shadow p-4 flex items-center">
          <div className="rounded-full bg-green-700 p-3 mr-3">
            <Calendar size={20} />
          </div>
          <div>
            <p className="text-sm opacity-80">Consultas</p>
            <p className="text-xl font-bold">{loading ? '...' : `${stats.todayAppointments} Hoje`}</p>
          </div>
        </div>
        
        <button 
          onClick={() => navigate('/dashboard/sessions')}
          className="bg-brand-700 text-white rounded-lg shadow p-4 flex items-center hover:bg-brand-800 transition-colors"
        >
          <div className="rounded-full bg-brand-800 p-3 mr-3">
            <Clock size={20} />
          </div>
          <div>
            <p className="text-sm opacity-80">Sessões</p>
            <p className="text-xl font-bold">{loading ? '...' : `${stats.sessions} Ativas`}</p>
          </div>
        </button>
        
        <div className="bg-orange-600 text-white rounded-lg shadow p-4 flex items-center">
          <div className="rounded-full bg-orange-700 p-3 mr-3">
            <FileText size={20} />
          </div>
          <div>
            <p className="text-sm opacity-80">Relatórios</p>
            <p className="text-xl font-bold">{loading ? '...' : `${stats.pendingReports} Pendentes`}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 